import { LinkGuideLayout } from '../components/LinkGuideLayout';

const sections = [
  ['fleet', 'Work with several MyChrons'],
  ['history', 'Choose existing sessions'],
  ['circuits', 'Choose the circuit for each paddock and date'],
  ['future', 'Choose future sessions for each logger'],
  ['syncing', 'Pause, resume and check for new recordings'],
  ['status', 'Check what has uploaded'],
  ['search', 'Find a session in Paddiq'],
] as const;

export function PaddiqLinkSessionsPage() {
  return (
    <LinkGuideLayout
      path="/resources/paddiq-link-sessions"
      title="Choose sessions and manage syncing in Link"
      lead="Choose which runs to transfer, keep driver and circuit assignments correct, and check that each upload reached Paddiq."
      sections={sections}
    >
      <section className="guide-step" id="fleet">
        <div>
          <h2>Work with several MyChrons.</h2>
          <p>
            In builds with the updated Overview, today’s circuit appears at the top. Choose a
            circuit to see its saved outline, direction and available distance. MyChron fleet and
            Recent transfers sit side by side on wider screens. Use <strong>Manage MyChrons</strong>{' '}
            to open your loggers, <strong>Sessions</strong> to browse a logger’s recordings, and{' '}
            <strong>All transfers</strong> to review sync activity. Saved transfer history stays
            visible while account data refreshes.
          </p>
          <p>
            The page header offers <strong>Pause all</strong> or a resume action showing the number
            of enabled MyChrons, plus <strong>Sync now</strong>. A MyChron’s connection status is
            separate from its sync status; click its row to browse sessions. Transfers show the
            recorded session time, driver and circuit, with download and upload stages. Completed
            uploads offer <strong>Open in Paddiq</strong>. The MyChrons table lists every registered
            MyChron and keeps its column labels visible when a wide fleet view scrolls.
          </p>
          <p>
            Link remembers browsing filters and position for each account and workspace on this
            computer. The circuit picker can suggest your most recent earlier circuit; inspect its
            outline and confirm <strong>Use this circuit</strong> before it applies. The selected
            circuit has a larger outline; use its pencil icon to change it. Setup ends with a
            summary and <strong>View sessions</strong>. Check your installed build’s controls and
            release availability before following a newer workflow.
          </p>
          <p>
            Saved MyChron settings use <strong>Wi-Fi</strong>, <strong>Driver &amp; kart</strong>,
            and <strong>Sessions</strong> tabs. Switching tabs keeps unsaved edits. Expand{' '}
            <strong>Saved hotspot settings</strong> to review recovery details; saving a preferred
            network is separate from applying it through <strong>Move to another network</strong>.
          </p>
          <p>
            The updated navigation opens each MyChron on its own page with its connection, driver
            and kart, settings and session list. Select recordings there and confirm the download.
            The profile menu, interface settings and update controls live at the bottom of the
            sidebar. Windows uses one compact search and title bar.
          </p>
          <p>
            Add and configure each logger in turn. One Wi-Fi adapter joins one logger hotspot at a
            time; once configured, multiple loggers can share the paddock network. Link identifies
            them by stable logger identity, not by IP address or which device responds first.
          </p>
          <p>
            Each saved logger keeps its connection status, driver/kart assignment, history and
            transfer queue. There is no fixed saved-device limit. Local discovery and transfers are
            bounded, so network quality and logger response time affect how quickly a large fleet is
            checked. This does not promise unlimited simultaneous hotspot connections or throughput.
          </p>
          <p>
            Use <strong>Select MyChrons</strong> for batch assignment or sync changes. Only selected
            loggers are changed; assigning a driver does not enable sync or rewrite existing
            transfers. Network and status filters help locate saved loggers without deleting those
            that are offline.
          </p>
        </div>
      </section>
      <section className="guide-step" id="history">
        <div>
          <h2>Choose existing sessions.</h2>
          <p>
            Start with a connected MyChron in the <a href="/resources/paddiq-link">setup guide</a>.
            Open its <strong>Sessions</strong> to see the logger’s recorded runs, newest first.
            <strong> Refresh sessions</strong> checks the logger again; browsing the list alone does
            not download telemetry files.
          </p>
          <p>
            <strong>New sessions only</strong> leaves files found during the first scan on the
            logger. It does not mean “recorded today.” <strong>Review existing sessions</strong>{' '}
            lets you choose older runs. Use <strong>Pull all existing sessions</strong> only when
            every run belongs in this workspace and you know its driver and circuit.
          </p>
          <ol className="guide-instructions">
            <li>Select the sessions you want to transfer.</li>
            <li>
              Choose <strong>Driver</strong> and an optional <strong>Kart</strong>, then use{' '}
              <strong>Apply to … newly selected</strong>. For a shared logger, assign one driver’s
              group, leave it checked, then select and assign the next group.
            </li>
            <li>
              Use <strong>Set circuit</strong> for any session missing a circuit. Confirm the layout
              and date; sessions using that date’s circuit choice share it.
            </li>
            <li>
              Review the assignments and choose <strong>Pull … selected</strong>. Every selected
              session needs a driver and circuit before Link can download it.
            </li>
          </ol>
          <p>
            Choosing <strong>Confirm none</strong> confirms that you do not want to pull any of
            those sessions. Already transferred sessions retain their recorded assignments.
          </p>
        </div>
      </section>
      <section className="guide-step" id="circuits">
        <div>
          <h2>Choose the circuit for each paddock and date.</h2>
          <ol className="guide-instructions">
            <li>
              Confirm the current paddock at the top of Overview. Open{' '}
              <strong>Today’s circuit</strong> or <strong>Circuit by date</strong> for the recording
              date you want to assign.
            </li>
            <li>
              Choose the venue and exact layout, including direction. Compare the saved outline
              before confirming. A suggested earlier circuit only applies when you explicitly choose{' '}
              <strong>Use this circuit</strong>.
            </li>
            <li>
              Check each MyChron’s driver and optional kart in <strong>Sync settings</strong>. The
              circuit follows this paddock’s selection for the recording date.
            </li>
          </ol>
          <p>
            Different paddocks keep separate dated choices. A new selection does not silently
            rewrite sessions already queued or uploaded. For a session marked
            <strong> Circuit update failed</strong>, use its explicit circuit repair action. If
            geometry does not load, check the message: an authentication error needs account renewal
            or sign-in; it is not evidence that the saved track or logger is missing.
          </p>
        </div>
      </section>
      <section className="guide-step" id="future">
        <div>
          <h2>Choose future sessions for each logger.</h2>
          <p>
            In builds with the fleet table and <strong>Sync settings</strong> (called Future
            settings in earlier builds), each saved logger has its own status, session history,
            future driver and optional compatible kart. Its circuit follows the current paddock’s
            dated choice. Use <strong>Sessions</strong> in that row to select older sessions
            manually. Offline loggers remain in the table, and Link transfers from up to three
            loggers at a time.
          </p>
          <p>
            Expand <strong>Timing &amp; review</strong> to check the starting point and timezone.{' '}
            <strong>Today</strong> sets the saved cutoff to 00:00 on the day you save, in the logger
            timezone you enter. <strong>From now</strong> starts at the exact save time. Link
            displays the saved UTC cutoff and timezone; the cutoff stays fixed when the date
            changes. Saving again sets a new cutoff. Check the logger’s clock before enabling this
            option. Missing or ambiguous clock times, including a repeated daylight-saving hour,
            stay for manual review. The latest resume or enable time also applies: choosing Today
            does not automatically backfill recordings made while syncing was paused. Select those
            recordings explicitly from Sessions.
          </p>
          <p>
            Choose an active driver and an optional compatible kart; Link verifies the current
            choices online before saving. Set the circuit from the paddock dashboard. Future
            defaults apply when a session first enters the local queue. Changing them does not
            silently reassign queued or uploaded sessions, and it does not change another logger’s
            settings or the shared circuit-by-date choice. Manual selections can include sessions
            before the cutoff with their own reviewed assignments.
          </p>
          <p>
            If a session shows <strong>Circuit update failed</strong>, open its logger’s Sessions
            and choose <strong>Repair circuit</strong>. Select a valid circuit and confirm{' '}
            <strong>Repair this session</strong> to finish that upload’s circuit update. Other
            sessions and future defaults stay unchanged.
          </p>
          <p>
            Enable automatic sessions for the selected logger and ensure workspace syncing is also
            on. Turning that logger’s automatic option off leaves manual selection available;
            already queued files retain their assignments. If these controls are absent, use the
            history options supported by your installed version. This guide does not imply that a
            public beta is available.
          </p>
        </div>
      </section>
      <section className="guide-step" id="syncing">
        <div>
          <h2>Pause, resume and check for new recordings.</h2>
          <p>
            Leave Link running and the computer awake on paddock Wi-Fi. Closing the window keeps
            Link in the Mac menu bar or Windows system tray. Quitting Link or putting the computer
            to sleep stops transfers.
          </p>
          <p>
            <strong>Pause all</strong> stops automatic downloads and cloud uploads after active work
            reaches a safe stopping point. You can still refresh the session list and pull
            explicitly selected history. The resume action starts only the MyChrons whose individual
            sync switches are enabled on this computer. Automatic transfers include only recordings
            made after resuming or enabling that MyChron. Older recordings remain historical,
            including files already held locally, until you select them deliberately. Your device
            switches are remembered across restart. Recordings with no timed laps stay out of
            automatic uploads, including power-on recordings. They remain available in Historical;
            an explicit manual override warns that pulling them may count toward usage.
          </p>
          <p>
            Resuming allows eligible work and cloud uploads to proceed; it does not continuously
            read the logger. A saved logger joining the network triggers a session-list check. Use{' '}
            <strong>Sync now</strong> for another check, or open the MyChron’s sessions and choose{' '}
            <strong>Refresh sessions</strong>. While paused, explicit selections can download, but
            their cloud uploads wait until syncing resumes. A visible row, a downloaded file and a
            completed cloud upload are different states.
          </p>
          <p>
            If internet access drops after a file finishes downloading, that original stays on this
            computer while its upload waits. Restore internet access and leave syncing on for
            retries. Sign-in and changes that verify account choices still need internet. Keep the
            original files and Link’s local data until uploads are confirmed.
          </p>
        </div>
      </section>
      <section className="guide-step" id="status">
        <div>
          <h2>Check what has uploaded.</h2>
          <p>
            Open <strong>Transfers</strong> to inspect uploads and errors. A file downloaded from
            the MyChron is not yet available in Paddiq until its cloud upload completes. If{' '}
            <strong>Circuit update failed</strong> appears, open that logger’s Sessions, choose{' '}
            <strong>Repair circuit</strong>, select the correct circuit, and confirm{' '}
            <strong>Repair this session</strong>.
          </p>
          <p>
            Your workspace can show saved loggers and history from other computers. That does not
            move their pending files here: restore internet and resume syncing on the computer
            holding the download. An online logger and <strong>Last synced</strong> are connection
            and session-list information, not confirmation that all uploads finished.
          </p>
          <p>
            For an extra copy outside Link, use{' '}
            <a href="/resources/paddiq-link-file-copies">Save session files to a folder</a>. For an
            upload that stays queued, follow the{' '}
            <a href="/resources/paddiq-link-troubleshooting">troubleshooting guide</a>.
          </p>
        </div>
      </section>
      <section className="guide-step" id="search">
        <div>
          <h2>Find a session in Paddiq.</h2>
          <p>
            In versions with search in the Link header, type at least two characters to find people,
            karts, uploaded sessions, or circuits in your signed-in Paddiq workspace. Try a driver’s
            name, session filename, or circuit name. Search needs internet access.
          </p>
          <p>
            Choosing a result opens the relevant page in your browser. Sign in there if asked and
            use the same Paddiq account and workspace. Lap analysis takes place in Paddiq; Link
            handles the logger connection and transfers.
          </p>
          <p>
            Search does not browse files waiting on the MyChron or this computer. Use the logger’s{' '}
            <strong>Sessions</strong> for those runs. If search is unavailable, check your
            connection and retry, or open Paddiq in your browser.
          </p>
        </div>
      </section>
    </LinkGuideLayout>
  );
}
